import { Flex, Select, SimpleGrid } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import {
  useForm,
  SubmitHandler,
  FieldValues,
  Controller,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Input } from "../../../components/atoms/Input";

import { Card } from "../../../components/Card";
import { createToast } from "../../../components/Feedback/Toast";
import { InternalHeader } from "../../../components/templates/InternalHeader";
import { api } from "../../../services/api";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitButton } from "../../../components/Button/SubmitButton";
import { FormControl } from "../../../components/Form/FormControl";
import { BackButton } from "../../../components/atoms/Button/BackButton";

const createPaymentFormSchema = yup.object().shape({
  name: yup
    .string()
    .required("Nome obrigatório")
    .min(3, "O nome deve conter no mínimo 3 caracteres"),
});

interface Reseller {
  id: string;
  name: string;
}
export function CreatePaymentLink() {
  const navigate = useNavigate();
  const { register, handleSubmit, formState, setValue, control } = useForm({
    resolver: yupResolver(createPaymentFormSchema),
  });
  const [hasDiscount, setHasDiscount] = useState(false);
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [hasCycle, setHasCycle] = useState(false);
  const [hasInstallments, setHasInstallments] = useState(false);
  const handleCreateCashierOutlets: SubmitHandler<FieldValues> = useCallback(
    async (formValue) => {
      try {
        await api.post("/paymentLinks", {
          name: formValue.name,
          description: formValue.description,
          billing_type: formValue.billing_type,
          value: formValue.value,
          total_months_discounted_installments:
            formValue.total_months_discounted_installments,
          discounted_installments: formValue.discounted_installments,
          installments: formValue.installments,
          payment_type: formValue.payment_type,
          reseller_id: formValue.reseller_id,
          cycle: formValue.cycle,
          plan_type: formValue.plan_type,
          months_to_first_payment: formValue.months_to_first_payment,
        });
        createToast({
          title: "Link do pagamento cadastrado com sucesso!",
          status: "success",
        });
        navigate("/links/pagamento");
      } catch (err) {
        createToast({
          title: "Não foi possível cadastrar o link de pagamento",
          status: "error",
        });
      }
    },
    [navigate]
  );
  useEffect(() => {
    api.get("/resellers").then((response) => setResellers(response.data));
  }, []);
  const { errors } = formState;
  return (
    <>
      <InternalHeader title="Cadastrar pagamentos" has_filter={false}>
        <BackButton onClick={() => navigate(-1)} />
      </InternalHeader>
      <Card
        body={
          <Flex
            as="form"
            flexDir="column"
            w="100%"
            onSubmit={handleSubmit(handleCreateCashierOutlets)}
            id="add-form"
          >
            <SimpleGrid columns={{ base: 1 }} spacing={2}>
              <Input
                label="Nome"
                error={errors.name}
                isRequired={true}
                {...register("name")}
              />
              <FormControl
                name="reseller_id"
                isRequired={false}
                label="Revendedor"
                error={errors.reseller_id}
              >
                <Controller
                  control={control}
                  name="reseller_id"
                  render={({ field }) => (
                    <Select {...field} placeholder="Selecione um Revendedor">
                      {resellers.map((reseller) => (
                        <option value={reseller.id}>{reseller.name}</option>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <Input
                label="Descrição"
                error={errors.description}
                isRequired={true}
                {...register("description")}
              />
              <FormControl
                name="billing_type"
                isRequired={true}
                label="Forma de Pagamento"
                error={errors.billing_type}
              >
                <Controller
                  control={control}
                  name="billing_type"
                  render={({ field }) => (
                    <Select {...field} placeholder="Selecione">
                      <option value="CARTÃO">Cartão</option>
                    </Select>
                  )}
                />
              </FormControl>
              <FormControl
                name="payment_type"
                isRequired={true}
                label="Tipo de Pagamento"
                error={errors.payment_type}
              >
                <Controller
                  control={control}
                  name="payment_type"
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Selecione"
                      onChange={(e) => {
                        field.onChange(e);
                        if (e.target.value === "assinatura") {
                          setHasCycle(true);
                          setHasInstallments(false);
                          setValue("installments", "");
                        } else {
                          if (e.target.value === "cobranca") {
                            setHasInstallments(true);
                            setHasCycle(false);
                            setValue("cycle", "");
                          } else {
                            setHasCycle(false);
                            setValue("cycle", "");
                            setHasInstallments(false);
                            setValue("installments", "");
                          }
                        }
                      }}
                    >
                      <option value="assinatura">Assinatura</option>
                      <option value="cobranca">Cobrança</option>
                    </Select>
                  )}
                />
              </FormControl>
              {hasCycle && (
                <FormControl
                  name="cycle"
                  isRequired={true}
                  label="Ciclo do Pagamento"
                  error={errors.cycle}
                >
                  <Controller
                    control={control}
                    name="cycle"
                    render={({ field }) => (
                      <Select {...field} placeholder="Selecione">
                        <option value="MONTHLY">Mensal</option>
                        <option value="QUARTERLY">Trimestral</option>
                        <option value="SEMIANNUALLY">Semestral</option>
                        <option value="YEARLY">Anual</option>
                      </Select>
                    )}
                  />
                </FormControl>
              )}
              {hasCycle && (
                <FormControl
                  name="plan_type"
                  isRequired={true}
                  label="Plano"
                  error={errors.plan_type}
                >
                  <Controller
                    control={control}
                    name="plan_type"
                    render={({ field }) => (
                      <Select {...field} placeholder="Selecione">
                        <option value="MONTHLY">Mensal</option>
                        <option value="QUARTERLY">Trimestral</option>
                        <option value="SEMIANNUALLY">Semestral</option>
                        <option value="YEARLY">Anual</option>
                      </Select>
                    )}
                  />
                </FormControl>
              )}
              {hasInstallments && (
                <FormControl
                  name="installments"
                  isRequired={true}
                  label="Parcelas"
                  error={errors.installments}
                >
                  <Controller
                    control={control}
                    name="installments"
                    render={({ field }) => (
                      <Select {...field} placeholder="Selecione">
                        <option value={1}>À vista</option>
                        <option value={2}>2x</option>
                        <option value={3}>3x</option>
                        <option value={4}>4x</option>
                        <option value={5}>5x</option>
                        <option value={6}>6x</option>
                        <option value={7}>7x</option>
                        <option value={8}>8x</option>
                        <option value={9}>9x</option>
                        <option value={10}>10x</option>
                        <option value={11}>11x</option>
                        <option value={12}>12x</option>
                      </Select>
                    )}
                  />
                </FormControl>
              )}
              {hasCycle && (
                <FormControl
                  name="total_months_discounted_installments"
                  isRequired={true}
                  label="Desconto"
                  error={errors.total_months_discounted_installments}
                >
                  <Controller
                    control={control}
                    name="total_months_discounted_installments"
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder="Selecione"
                        onChange={(e) => {
                          field.onChange(e);
                          if (Number(e.target.value) > 0) {
                            setHasDiscount(true);
                          } else {
                            setHasDiscount(false);
                            setValue("discounted_installments", "");
                          }
                        }}
                      >
                        <option value={0}>Sem desconto</option>
                        <option value={1}>Desconto de 1 mês</option>
                        <option value={2}>Desconto de 2 meses</option>
                        <option value={3}>Desconto de 3 meses</option>
                        <option value={4}>Desconto de 4 meses</option>
                        <option value={5}>Desconto de 5 meses</option>
                      </Select>
                    )}
                  />
                </FormControl>
              )}
              <Input
                label="Meses para o primeiro pagamento"
                defaultValue={0}
                error={errors.months_to_first_payment}
                isRequired={true}
                min={0}
                type="number"
                {...register("months_to_first_payment")}
              />
              <Input
                label="Valor"
                error={errors.value}
                isRequired={true}
                {...register("value")}
                leftAddon="R$"
                onChange={(e) => {
                  var value = e.target.value;
                  if (value.length > 2) {
                    value = value
                      .replace(/\D+/g, "")
                      .replace(/([0-9]{2})$/g, ",$1");
                    e.target.value = value;
                    setValue("value", value);
                  }
                }}
              />
              {hasDiscount && (
                <Input
                  label="Parcela com desconto"
                  error={errors.discounted_installments}
                  isRequired={true}
                  {...register("discounted_installments")}
                  leftAddon="R$"
                  onChange={(e) => {
                    var value = e.target.value;
                    if (value.length > 2) {
                      value = value
                        .replace(/\D+/g, "")
                        .replace(/([0-9]{2})$/g, ",$1");
                      e.target.value = value;
                      setValue("discounted_installments", value);
                    }
                  }}
                />
              )}
            </SimpleGrid>
          </Flex>
        }
        footer={<SubmitButton formState={formState} />}
      />
    </>
  );
}
